import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslations, useLocale } from "use-intl";
import { z } from "zod";

import { useAppForm } from "@/components/forms/app-form";
import { Label } from "@/components/ui/label";
import { client } from "@/utils/orpc.client";
import { contactInputSchema, createContactInputSchema } from "@garimpa/lib/formSchemas";

const contactSubjects = ["general", "storeRequest"] as const;

interface ContactContentProps {
  initialSubject?: (typeof contactSubjects)[number];
}

function buildStoreRequestMessage(input: {
  storeUrl: string;
  requesterDetails: string;
  reason: string;
}) {
  return [
    "Store request",
    `Store URL: ${input.storeUrl}`,
    `Requester details: ${input.requesterDetails}`,
    `Reason: ${input.reason}`,
  ].join("\n\n");
}

export function ContactContent({ initialSubject = "general" }: ContactContentProps) {
  const t = useTranslations("shared.contact");
  const locale = useLocale();
  const contactFormSchema = createContactInputSchema({
    nameRequired: t("validation.nameRequired"),
    emailInvalid: t("validation.emailInvalid"),
    messageMin: t("validation.messageMin"),
    messageMax: t("validation.messageMax"),
  })
    .omit({ locale: true, message: true })
    .extend({
      subject: z.enum(contactSubjects, { message: t("validation.subjectRequired") }),
      message: z.string(),
      storeUrl: z.string(),
      requesterDetails: z.string(),
      reason: z.string(),
    })
    .superRefine((input, context) => {
      if (input.subject === "general") {
        const message = contactInputSchema.shape.message.safeParse(input.message);
        if (!message.success) {
          context.addIssue({
            code: "custom",
            path: ["message"],
            message: t("validation.messageMin"),
          });
        }
        return;
      }

      if (!z.string().url().safeParse(input.storeUrl).success) {
        context.addIssue({
          code: "custom",
          path: ["storeUrl"],
          message: t("validation.storeUrlInvalid"),
        });
      }
      if (input.requesterDetails.trim().length < 3) {
        context.addIssue({
          code: "custom",
          path: ["requesterDetails"],
          message: t("validation.requesterDetailsMin"),
        });
      }
      if (input.reason.trim().length < 10) {
        context.addIssue({ code: "custom", path: ["reason"], message: t("validation.reasonMin") });
      }
    });

  const sendMutation = useMutation({
    mutationFn: (input: z.infer<typeof contactInputSchema>) => client.contact.sendMessage(input),
    onSuccess: () => {
      toast.success(t("success"));
    },
    onError: () => {
      toast.error(t("error"));
    },
  });

  const form = useAppForm({
    defaultValues: {
      subject: initialSubject,
      name: "",
      email: "",
      message: "",
      storeUrl: "",
      requesterDetails: "",
      reason: "",
    },
    validators: { onSubmit: contactFormSchema },
    onSubmit: async ({ value }) => {
      const message =
        value.subject === "storeRequest" ? buildStoreRequestMessage(value) : value.message;
      const input = contactInputSchema.parse({
        name: value.name,
        email: value.email,
        message,
        locale,
      });
      await sendMutation.mutateAsync(input);
      form.reset();
    },
  });

  return (
    <form.AppForm>
      <form.Form className="space-y-4 max-w-lg">
        <form.AppField name="subject">
          {(field) => (
            <div className="space-y-1.5">
              <Label htmlFor={field.name}>{t("subject")}</Label>
              <select
                id={field.name}
                name={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(event) =>
                  field.handleChange(event.target.value as (typeof contactSubjects)[number])
                }
                className="bg-input/30 border-input focus-visible:border-ring focus-visible:ring-ring/30 h-11 sm:h-10 w-full rounded-lg border px-3.5 text-sm outline-none focus-visible:ring-2"
              >
                <option value="general">{t("subjects.general")}</option>
                <option value="storeRequest">{t("subjects.storeRequest")}</option>
              </select>
              <field.FieldErrors />
            </div>
          )}
        </form.AppField>

        <form.AppField name="name">
          {(field) => <field.TextField label={t("name")} placeholder={t("namePlaceholder")} />}
        </form.AppField>

        <form.AppField name="email">
          {(field) => (
            <field.TextField label={t("email")} type="email" placeholder={t("emailPlaceholder")} />
          )}
        </form.AppField>

        <form.Subscribe selector={(state) => state.values.subject}>
          {(subject) =>
            subject === "storeRequest" ? (
              <>
                <div className="rounded-xl bg-muted/50 px-4 py-3 text-sm text-muted-foreground">
                  <p className="font-semibold text-foreground">{t("storeRequest.title")}</p>
                  <p className="mt-1">{t("storeRequest.description")}</p>
                </div>
                <form.AppField name="storeUrl">
                  {(field) => (
                    <field.TextField
                      label={t("storeRequest.storeUrl")}
                      placeholder={t("storeRequest.storeUrlPlaceholder")}
                      type="url"
                    />
                  )}
                </form.AppField>
                <form.AppField name="requesterDetails">
                  {(field) => (
                    <field.TextareaField
                      label={t("storeRequest.requesterDetails")}
                      placeholder={t("storeRequest.requesterDetailsPlaceholder")}
                      rows={3}
                    />
                  )}
                </form.AppField>
                <form.AppField name="reason">
                  {(field) => (
                    <field.TextareaField
                      label={t("storeRequest.reason")}
                      placeholder={t("storeRequest.reasonPlaceholder")}
                      rows={4}
                    />
                  )}
                </form.AppField>
              </>
            ) : (
              <form.AppField name="message">
                {(field) => (
                  <field.TextareaField
                    label={t("message")}
                    rows={5}
                    className="resize-y"
                    placeholder={t("messagePlaceholder")}
                  />
                )}
              </form.AppField>
            )
          }
        </form.Subscribe>

        <form.SubmitButton submittingLabel={t("send")}>{t("send")}</form.SubmitButton>
      </form.Form>
    </form.AppForm>
  );
}
